import { currentTokens, fileUrl } from "@shared/api";
import { repairOggOpus } from "./ogg-opus";
import { audioContext, useVolumes } from "./volume";
import { encodeWav } from "./wav";

/**
 * Звуковая панель: проигрывание звука, который сыграли в звонке.
 *
 * Своя громкость, отдельно от голосов и окликов — как «громкость звуковой
 * панели» в Discord: кто-то хочет её тише, а голоса оставить как есть.
 * Громкость самого звука (её ставит сервер) умножается на свою.
 */
export const SOUNDBOARD_KEY = "soundboard";

export const SOUNDBOARD_DEFAULT = 1;

/** Дольше этого звук панели не звучит, даже если файл длиннее. */
const LONGEST = 6;

export function soundboardVolume(): number {
  return useVolumes.getState()[SOUNDBOARD_KEY] ?? SOUNDBOARD_DEFAULT;
}

/**
 * Звук после загрузки: разобранный — для Web Audio, сырой файл — на случай, когда
 * разобрать не вышло. Firefox разбирает в `decodeAudioData` не всё, что сам же
 * играет в `<audio>`: такой звук пойдёт через элемент, а не пропадёт молча.
 */
interface Loaded {
  buffer: AudioBuffer | null;
  blob: Blob | null;
}

const loaded = new Map<string, Promise<Loaded>>();

/**
 * Файл звука — с токеном, как картинки (`FileImage`): сервер отдаёт файлы
 * только вошедшим. Звук кэшируется по файлу; неудача не кэшируется — следующее
 * нажатие попробует снова.
 */
function load(fileId: string): Promise<Loaded> {
  const known = loaded.get(fileId);
  if (known) return known;

  const loading = (async (): Promise<Loaded> => {
    let blob: Blob;
    try {
      const token = currentTokens().access;
      const response = await fetch(fileUrl(fileId), {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      blob = await response.blob();
    } catch {
      // Сеть — не повод помнить звук пустым: следующее нажатие попробует снова.
      loaded.delete(fileId);
      return { buffer: null, blob: null };
    }
    return decode(fileId, blob);
  })();

  loaded.set(fileId, loading);
  // Не держим в памяти больше пары десятков: звуков на сервере не больше 24.
  if (loaded.size > 48) loaded.delete(loaded.keys().next().value!);
  return loading;
}

/**
 * Разобрать звук. Не вышло — может, это Ogg Opus с позициями не в 48 кГц (Firefox
 * таких не берёт, Chromium берёт): чиним и пробуем снова. Не помогло — звук пойдёт
 * через `<audio>`.
 */
async function decode(fileId: string, blob: Blob): Promise<Loaded> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  try {
    // Копия: `decodeAudioData` забирает буфер себе, а байты ещё нужны для починки.
    return { buffer: await audioContext().decodeAudioData(bytes.slice().buffer), blob };
  } catch (error) {
    const repaired = repairOggOpus(bytes);
    if (repaired) {
      const fixed = new Blob([repaired], { type: blob.type });
      try {
        return { buffer: await audioContext().decodeAudioData(repaired.slice().buffer), blob: fixed };
      } catch {
        blob = fixed;
      }
    }
    console.warn("[звуки] Web Audio не разобрал файл, играю через <audio>", fileId, blob.type, error);
    return { buffer: null, blob };
  }
}

/** Разобранный звук сервера — для волны в окне правки. */
export function soundBuffer(fileId: string): Promise<AudioBuffer | null> {
  return load(fileId).then((sound) => sound.buffer);
}

/** Заранее разобрать звук — чтобы первое нажатие не ждало сеть. */
export function preloadSound(fileId: string) {
  void load(fileId);
}

/** Звук сервера: громкость звука, умноженная на свою громкость панели. */
export function playSound(fileId: string, volume: number) {
  void load(fileId).then(({ buffer, blob }) => {
    if (buffer) sound(buffer, volume);
    else if (blob) element(blob, volume);
  });
}

/** Запасной путь — `<audio>`: громкость та же, вывод — устройство по умолчанию, как у Web Audio. */
function element(blob: Blob, volume: number) {
  const level = Math.max(0, Math.min(1, volume)) * soundboardVolume();
  if (level <= 0) return;
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  audio.volume = Math.min(1, level);
  const release = () => URL.revokeObjectURL(url);
  audio.onended = release;
  audio.onerror = release;
  void audio.play().catch(release);
}

/** Свой файл до загрузки — послушать перед тем, как добавить. */
export function playLocal(file: Blob, volume: number) {
  void file
    .arrayBuffer()
    .then((bytes) => audioContext().decodeAudioData(bytes))
    .then((buffer) => sound(buffer, volume))
    .catch(() => {});
}

export interface SoundPreview {
  /** Громкость на ходу — ползунок в окне двигают, пока звук играет. */
  setVolume: (volume: number) => void;
  /** Остановить; `onEnded` при этом не зовётся. */
  stop: () => void;
  /** Где сейчас звук, в секундах от начала файла, — для курсора на волне. */
  position: () => number;
}

/**
 * Предпросмотр в окне звука: только у себя, с громкостью на ходу, остановкой
 * и местом в звуке. Громкость — та же, что у панели в звонке. `range` — какой
 * кусок играть, в секундах: с курсора до конца отрезка.
 */
export function previewSound(
  buffer: AudioBuffer,
  volume: number,
  onEnded: () => void,
  range: { from: number; to: number } = { from: 0, to: buffer.duration },
): SoundPreview {
  const context = audioContext();
  void context.resume().catch(() => {});
  const level = (value: number) => Math.max(0, Math.min(1, value)) * soundboardVolume();
  const gain = context.createGain();
  gain.gain.value = level(volume);
  gain.connect(context.destination);

  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(gain);
  const length = Math.max(0, Math.min(range.to - range.from, LONGEST));
  const started = context.currentTime;
  let stopped = false;
  source.onended = () => {
    gain.disconnect();
    if (!stopped) onEnded();
  };
  source.start(0, range.from, length);

  return {
    setVolume: (next) => gain.gain.setTargetAtTime(level(next), context.currentTime, 0.02),
    stop: () => {
      stopped = true;
      try {
        source.stop();
      } catch {
        // Уже доиграл — останавливать нечего.
      }
    },
    position: () => range.from + Math.min(length, Math.max(0, context.currentTime - started)),
  };
}

/**
 * Кусок звука с `from` по `to` секунд — файлом WAV для загрузки: один канал
 * (стерео сводится), не выше 48 кГц. Иначе пять секунд не влезли бы в 512 КБ.
 */
export async function cutSound(buffer: AudioBuffer, from: number, to: number, name: string): Promise<File> {
  const rate = Math.min(48_000, buffer.sampleRate);
  const offline = new OfflineAudioContext(1, Math.max(1, Math.round((to - from) * rate)), rate);
  const source = offline.createBufferSource();
  source.buffer = buffer;
  source.connect(offline.destination);
  source.start(0, from, to - from);
  const rendered = await offline.startRendering();
  return new File([encodeWav(rendered.getChannelData(0), rate)], `${name.replace(/\.[^.]+$/, "")}.wav`, {
    type: "audio/wav",
  });
}

function sound(buffer: AudioBuffer, volume: number) {
  const level = Math.max(0, Math.min(1, volume)) * soundboardVolume();
  if (level <= 0) return;

  const context = audioContext();
  const gain = context.createGain();
  gain.gain.value = level;
  gain.connect(context.destination);

  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(gain);
  source.onended = () => gain.disconnect();
  source.start(0, 0, Math.min(buffer.duration, LONGEST));
}
